import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, useToast } from '@w7/ui'
import { useState, type FormEvent } from 'react'
import { cmApi, type SecurityBlockedDeviceRow } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { enabledChip } from './securityUi'
import { SecurityCrudPage } from './SecurityCrudPage'

export function BlockedDeviceIdsPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [deviceId, setDeviceId] = useState('')
  const [comment, setComment] = useState('')

  const saveM = useMutation({
    mutationFn: () =>
      cmApi.securityBlockedDeviceSave(authHeaders(), {
        deviceId: deviceId.trim(),
        comment: comment.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Blocked device saved')
      setDeviceId('')
      setComment('')
      await qc.invalidateQueries({ queryKey: ['cm', 'security', 'blocked-device'] })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  return (
    <SecurityCrudPage<SecurityBlockedDeviceRow>
      title="Blocked device IDs"
      subtitle="Block player device identifiers"
      queryKey="blocked-device"
      emptyHint="No blocked devices yet."
      listFn={cmApi.securityBlockedDeviceList}
      deleteFn={cmApi.securityBlockedDeviceDelete}
      columns={[
        { key: 'device', header: 'Device ID', cell: (r) => r.deviceId },
        { key: 'comment', header: 'Comment', cell: (r) => r.comment ?? '—' },
        { key: 'enabled', header: 'Enabled', cell: (r) => enabledChip(r.enabled) },
        { key: 'by', header: 'By', cell: (r) => r.createdBy ?? '—' },
      ]}
      form={
        <form
          className="cm-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            saveM.mutate()
          }}
        >
          <div className="cm-form-grid">
            <Field label="Device ID" required>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  required
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  placeholder="Device ID"
                />
              )}
            </Field>
            <Field label="Comment">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional"
                />
              )}
            </Field>
            <div className="cm-form-actions">
              <Button type="submit" busy={saveM.isPending}>
                Add / update
              </Button>
            </div>
          </div>
        </form>
      }
    />
  )
}
